import { useEffect, useRef, useState } from "react";

/**
 * TickerCounter
 * Modes:
 *   - mode="rate": deterministic time-based growth.
 *       value = baseValue + ((Date.now() - startTimestamp) * perDayRate) / 86_400_000
 *   - mode="randomInterval": value increments by `incrementBy` at a random
 *       interval between minIntervalMs and maxIntervalMs. Persisted to
 *       localStorage so it keeps growing across reloads.
 *
 * On mount it always plays a slot-machine reveal animation.
 */
export const TickerCounter = ({
  // shared
  baseValue,
  prefix = "",
  suffix = "",
  decimals = 0,
  slotDurationMs = 1700,
  className = "",
  testId,

  // rate mode
  mode = "rate",
  startTimestamp,
  perDayRate,
  liveTickMs = 1000,

  // randomInterval mode
  incrementBy = 1,
  incrementMin,
  incrementMax,
  minIntervalMs = 5000,
  maxIntervalMs = 30000,
  storageKey,
}) => {
  const [display, setDisplay] = useState(baseValue);
  const [phase, setPhase] = useState("slot");
  const [pulseKey, setPulseKey] = useState(0);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const valueRef = useRef(baseValue);
  const lastIntegerRef = useRef(Math.floor(baseValue));

  // ---- helpers ---------------------------------------------------------
  const computeRateTarget = () =>
    baseValue +
    ((Date.now() - startTimestamp) * perDayRate) / 86_400_000;

  const readStoredValue = () => {
    if (typeof window === "undefined" || !storageKey) return null;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Only honor stored value if it's >= base (monotonic growth)
      if (typeof parsed?.value === "number" && parsed.value >= baseValue) {
        return parsed.value;
      }
    } catch (_) {
      /* ignore parse errors */
    }
    return null;
  };

  const writeStoredValue = (val) => {
    if (typeof window === "undefined" || !storageKey) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ value: val, t: Date.now() })
      );
    } catch (_) {
      /* ignore quota errors */
    }
  };

  const initialTarget = () => {
    if (mode === "rate") return computeRateTarget();
    // randomInterval
    const stored = readStoredValue();
    return stored ?? baseValue;
  };

  // ---- slot reveal -----------------------------------------------------
  useEffect(() => {
    const finalTarget = initialTarget();
    valueRef.current = finalTarget;
    const targetStr = Math.floor(finalTarget).toString();
    const digitCount = targetStr.length;
    const start = performance.now();

    const loop = (t) => {
      const progress = Math.min(1, (t - start) / slotDurationMs);
      const eased = 1 - Math.pow(1 - progress, 2);
      const revealedCount = Math.floor(eased * digitCount);
      let scrambled = "";
      for (let i = 0; i < digitCount; i++) {
        if (i < revealedCount) {
          scrambled += targetStr[i];
        } else {
          scrambled += Math.floor(Math.random() * 10).toString();
        }
      }
      setDisplay(parseInt(scrambled, 10));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setDisplay(finalTarget);
        lastIntegerRef.current = Math.floor(finalTarget);
        valueRef.current = finalTarget;
        setPhase("live");
      }
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- live ticking ----------------------------------------------------
  useEffect(() => {
    if (phase !== "live") return;

    if (mode === "rate") {
      const id = setInterval(() => {
        const next = computeRateTarget();
        const nextInt = Math.floor(next);
        if (nextInt > lastIntegerRef.current) {
          lastIntegerRef.current = nextInt;
          setPulseKey((k) => k + 1);
        }
        valueRef.current = next;
        setDisplay(next);
      }, liveTickMs);
      return () => clearInterval(id);
    }

    // randomInterval mode
    const pickIncrement = () => {
      if (
        typeof incrementMin === "number" &&
        typeof incrementMax === "number" &&
        incrementMax >= incrementMin
      ) {
        return (
          incrementMin +
          Math.floor(Math.random() * (incrementMax - incrementMin + 1))
        );
      }
      return incrementBy;
    };

    const scheduleNext = () => {
      const delay =
        minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
      timeoutRef.current = setTimeout(() => {
        const next = valueRef.current + pickIncrement();
        valueRef.current = next;
        lastIntegerRef.current = Math.floor(next);
        setPulseKey((k) => k + 1);
        setDisplay(next);
        writeStoredValue(next);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const formatted = (decimals > 0 ? display : Math.floor(display)).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }
  );

  return (
    <span
      data-testid={testId}
      data-phase={phase}
      className={`tabular-nums inline-flex items-baseline ${className}`}
    >
      <span
        key={phase === "live" ? `tick-${pulseKey}` : "slot"}
        className={
          phase === "slot" ? "rm-ticker-slot" : "rm-ticker-flash"
        }
      >
        {prefix}
        {formatted}
        {suffix}
      </span>
    </span>
  );
};
